import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { type ColumnDef } from "@tanstack/react-table";
import { IconChartBar, IconExternalLink, IconPhoto } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { IconButton } from "@/components/dashboard/icon-button";
import { ActionDialog, DialogTextButton } from "@/components/common/ActionDialog";
import { AsideRow, Field, FieldRow, SettingsGrid } from "@/components/settings/settings-kit";
import { ApprovedContentPortraitTile } from "@/components/brand/content-programs/ApprovedContentPortraitTile";
import { MediaSheet, MediaSheetSkeleton, RatioSection, ratioClass } from "@/components/content-programs/media-tile";
import { useRatioGroups } from "@/hooks/useRatioGroups";
import MetaLaunchPausedNotice, { buildMetaAdsManagerUrl } from "@/components/brand/MetaLaunchPausedNotice";
import { MetaTargetPicker, NO_TARGET, type MetaTarget } from "@/components/brand/MetaTargetPicker";
import {
  createMetaCampaignProgram,
  launchMetaCampaignFromContent,
  listMetaCampaignAds,
  listMetaCampaignContent,
  listMetaCampaignPrograms,
  type MetaCampaignContent,
  type MetaCampaignLaunchedAd,
  type MetaCampaignProgram,
} from "@/services/metaAdsCampaignProgram.service";
import { formatAmount, formatDate } from "@/utils/formatters";
import { cn } from "@/lib/utils";
import { shortId } from "./insights";

function errMessage(e: unknown): string {
  if (e instanceof AxiosError) {
    const d = e.response?.data as { message?: string | string[] } | undefined;
    if (d?.message) return Array.isArray(d.message) ? d.message.join(", ") : d.message;
    return e.message || "Request failed";
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

interface RunMetaCampaignTabProps {
  pages: { id: string; name?: string }[];
  pagesLoading: boolean;
  defaultFacebookPageId: string;
  defaultAdAccountId?: string | null;
  /** Sales ads need a pixel; without one the objective stays on traffic. */
  metaPixelId?: string | null;
  /** On a program's own page the program is given, so the picker is not shown. */
  fixedProgramId?: string;
  /** Approved creative to start with chosen; its draft settings prefill the form. */
  initialContentId?: string | null;
  onLaunched?: () => void;
}

export default function RunMetaCampaignTab({
  pages,
  pagesLoading,
  defaultFacebookPageId,
  defaultAdAccountId,
  metaPixelId = null,
  fixedProgramId,
  initialContentId,
  onLaunched,
}: RunMetaCampaignTabProps) {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<MetaCampaignProgram[]>([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programId, setProgramId] = useState(fixedProgramId ?? "");
  const [content, setContent] = useState<MetaCampaignContent[]>([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState("");
  const [ads, setAds] = useState<MetaCampaignLaunchedAd[]>([]);
  const [adsLoading, setAdsLoading] = useState(false);

  const [newProgramOpen, setNewProgramOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newRate, setNewRate] = useState("10");
  const [creatingProgram, setCreatingProgram] = useState(false);

  const [facebookPageId, setFacebookPageId] = useState("");
  const [dailyBudget, setDailyBudget] = useState("15");
  const [campaignName, setCampaignName] = useState("");
  const [countries, setCountries] = useState("US");
  const [objective, setObjective] = useState<"OUTCOME_TRAFFIC" | "OUTCOME_SALES">(metaPixelId ? "OUTCOME_SALES" : "OUTCOME_TRAFFIC");
  const [ageMin, setAgeMin] = useState("18");
  const [ageMax, setAgeMax] = useState("65");
  const [target, setTarget] = useState<MetaTarget>(NO_TARGET);
  const [launching, setLaunching] = useState(false);

  // Sales is only possible with a pixel; fall back when it is removed.
  useEffect(() => {
    if (!metaPixelId) setObjective("OUTCOME_TRAFFIC");
  }, [metaPixelId]);

  useEffect(() => {
    if (defaultFacebookPageId) setFacebookPageId(defaultFacebookPageId);
  }, [defaultFacebookPageId]);

  const loadPrograms = useCallback(async () => {
    setProgramsLoading(true);
    try {
      const rows = await listMetaCampaignPrograms();
      setPrograms(rows);
      setProgramId((prev) => fixedProgramId ?? (prev || (rows.length === 1 ? rows[0].id : "")));
    } catch (e) {
      toast.error(errMessage(e));
      setPrograms([]);
    } finally {
      setProgramsLoading(false);
    }
  }, [fixedProgramId]);

  useEffect(() => {
    void loadPrograms();
  }, [loadPrograms]);

  const loadContent = useCallback(async () => {
    if (!programId) {
      setContent([]);
      setSelectedContentId("");
      return;
    }
    setContentLoading(true);
    try {
      const rows = await listMetaCampaignContent(programId);
      setContent(rows);
      const preset = initialContentId ? rows.find((c) => c.id === initialContentId) : undefined;
      setSelectedContentId(preset ? preset.id : "");
      // A TikTok ad request drafted the ad: carry its campaign name and budget.
      const draft = preset?.launchedAd?.status === "DRAFT" ? preset.launchedAd.adConfig : null;
      if (draft?.dailyBudget) setDailyBudget(String(draft.dailyBudget));
      if (draft?.countries?.length) setCountries(draft.countries.join(", "));
      if (draft?.campaignName) setCampaignName(draft.campaignName);
      if (draft?.objective) setObjective(draft.objective === "OUTCOME_SALES" && metaPixelId ? "OUTCOME_SALES" : "OUTCOME_TRAFFIC");
      if (draft?.ageMin) setAgeMin(String(draft.ageMin));
      if (draft?.ageMax) setAgeMax(String(draft.ageMax));
      if (draft?.targetCampaignId) {
        setTarget({ campaignId: draft.targetCampaignId, campaignName: null, adsetId: draft.targetAdsetId ?? null, adsetName: null });
      }
    } catch (e) {
      toast.error(errMessage(e));
      setContent([]);
    } finally {
      setContentLoading(false);
    }
  }, [programId, initialContentId, metaPixelId]);

  useEffect(() => {
    void loadContent();
  }, [loadContent]);

  const loadAds = useCallback(async () => {
    if (!programId) {
      setAds([]);
      return;
    }
    setAdsLoading(true);
    try {
      setAds(await listMetaCampaignAds(programId));
    } catch (e) {
      toast.error(errMessage(e));
      setAds([]);
    } finally {
      setAdsLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    void loadAds();
  }, [loadAds]);

  const handleCreateProgram = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = newTitle.trim();
    const rate = Number.parseFloat(newRate);
    if (!title) {
      toast.error("Enter a program name");
      return;
    }
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
      toast.error("Commission must be between 0 and 100");
      return;
    }
    setCreatingProgram(true);
    try {
      const created = await createMetaCampaignProgram({ title, commissionRate: rate });
      toast.success("Program created");
      setNewTitle("");
      setNewRate("10");
      setNewProgramOpen(false);
      setPrograms((prev) => [created, ...prev]);
      setProgramId(created.id);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setCreatingProgram(false);
    }
  };

  const program = programs.find((p) => p.id === programId) ?? null;
  const selected = content.find((c) => c.id === selectedContentId) ?? null;
  const { orderedGroups, loading: ratiosLoading, getRatioLabel } = useRatioGroups(content);
  const page = pages.find((p) => p.id === facebookPageId) ?? null;
  const budget = Number.parseFloat(dailyBudget);
  const countryList = countries
    .split(",")
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
  const ageLo = Number.parseInt(ageMin, 10);
  const ageHi = Number.parseInt(ageMax, 10);
  const ageOk = Number.isFinite(ageLo) && Number.isFinite(ageHi) && ageLo >= 13 && ageHi <= 65 && ageLo <= ageHi;
  const canLaunch = Boolean(programId && selected && facebookPageId.trim()) && ageOk && !launching;

  const handleLaunch = async () => {
    if (!programId || !selected || !facebookPageId.trim()) return;
    setLaunching(true);
    try {
      const result = await launchMetaCampaignFromContent(programId, {
        contentId: selected.id,
        adConfig: {
          facebookPageId: facebookPageId.trim(),
          ...(campaignName.trim() ? { campaignName: campaignName.trim() } : {}),
          ...(Number.isFinite(budget) && budget >= 1 ? { dailyBudget: budget } : {}),
          ...(countryList.length ? { countries: countryList } : {}),
          objective,
          ...(ageOk ? { ageMin: ageLo, ageMax: ageHi } : {}),
          ...(target.campaignId ? { targetCampaignId: target.campaignId } : {}),
          ...(target.adsetId ? { targetAdsetId: target.adsetId } : {}),
        },
      });
      toast.success(`Ad ${shortId(result.metaAdId)} created in Meta, paused`);
      await loadContent();
      await loadAds();
      onLaunched?.();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setLaunching(false);
    }
  };

  const adsManagerUrl = buildMetaAdsManagerUrl(defaultAdAccountId);

  const launchedColumns = useMemo<ColumnDef<MetaCampaignLaunchedAd>[]>(
    () => [
      {
        id: "content",
        header: "Content",
        cell: ({ row }) => {
          const c = row.original.metaAdsCampaignContent;
          return (
            <span className="flex min-w-0 items-center gap-2.5">
              <span className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded bg-muted text-muted-foreground">
                {c?.thumbnailUrl ? (
                  <img src={c.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <IconPhoto size={14} stroke={1.75} />
                )}
              </span>
              <span className="block max-w-[16rem] truncate font-medium text-foreground" title={c?.title ?? ""}>
                {c?.title || "Untitled"}
              </span>
            </span>
          );
        },
      },
      {
        id: "affiliate",
        header: "Affiliate",
        meta: { fit: true },
        cell: ({ row }) => <span className="text-foreground">{shortId(row.original.creatorAffiliateId)}</span>,
      },
      {
        id: "status",
        header: "Status",
        meta: { fit: true },
        cell: ({ row }) => (
          <StatusText status={row.original.launchError ? "REJECTED" : row.original.metaAdId ? "PAUSED" : "PENDING"} />
        ),
      },
      {
        id: "launched",
        header: "Launched",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">{formatDate(row.original.createdAt)}</span>
        ),
      },
      {
        id: "open",
        header: "",
        meta: { fit: true, cellClassName: "text-right", skeleton: <span /> },
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1">
            {row.original.metaAdId && (
              <IconButton
                label="Ad performance"
                icon={<IconChartBar stroke={1.75} />}
                onClick={() => navigate(`/brand/meta-ads/performance?ad=${row.original.metaAdId}`)}
              />
            )}
            <IconButton
              label="Open in Ads Manager"
              icon={<IconExternalLink stroke={1.75} />}
              onClick={() => window.open(adsManagerUrl, "_blank", "noopener,noreferrer")}
            />
          </span>
        ),
      },
    ],
    [adsManagerUrl, navigate]
  );

  return (
    <div className="space-y-4">
      <SettingsGrid
        form={
          <>
            {!fixedProgramId && (
            <DashboardCard
              title="Program"
              bodyClassName="px-5 pb-5"
              action={
                <button
                  type="button"
                  onClick={() => setNewProgramOpen(true)}
                  className="rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  New program
                </button>
              }
            >
              {!programsLoading && programs.length === 0 ? (
                <EmptyState
                  bordered={false}
                  title="No Meta programs yet"
                  body="A program sets the commission affiliates earn on sales from these ads"
                  action={<Button onClick={() => setNewProgramOpen(true)}>New program</Button>}
                  className="py-8"
                />
              ) : (
                <Field label="Program" htmlFor="meta-program" help="Affiliates whose content you launch earn this program's commission">
                  <Select value={programId} onValueChange={(v) => v && setProgramId(v)} disabled={programsLoading}>
                    <SelectTrigger id="meta-program">
                      <SelectValue placeholder={programsLoading ? "Loading programs" : "Choose a program"} />
                    </SelectTrigger>
                    <SelectContent>
                      {programs.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.title} ({p.commissionRate}% commission)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </DashboardCard>
            )}

            <DashboardCard title="Content" bodyClassName="px-5 pb-5">
              {!programId ? (
                <p className="text-sm text-muted-foreground">Choose a program to see its approved content.</p>
              ) : contentLoading || (ratiosLoading && content.length > 0) ? (
                <MediaSheetSkeleton count={Math.min(Math.max(content.length, 3), 6)} />
              ) : content.length === 0 ? (
                <EmptyState
                  bordered={false}
                  title="No approved content yet"
                  body="Approve submissions under Content programs, then launch them here"
                  className="py-8"
                />
              ) : (
                <div className="space-y-5">
                  {orderedGroups.map((group) => (
                    <RatioSection key={group.key} ratio={group.key} count={group.items.length}>
                      <MediaSheet ratio={group.key}>
                        {group.items.map((c) => (
                          <ApprovedContentPortraitTile
                            key={c.id}
                            creative={c}
                            selected={selectedContentId === c.id}
                            radioName="meta-content-run-tab"
                            ratio={getRatioLabel(c.id)}
                            onSelect={() => setSelectedContentId(c.id)}
                          />
                        ))}
                      </MediaSheet>
                    </RatioSection>
                  ))}
                </div>
              )}
            </DashboardCard>

            <DashboardCard title="Ad settings" bodyClassName="space-y-5 px-5 pb-5">
              <Field label="Facebook page" htmlFor="launch-page" help="The page the ad is published as">
                {pages.length === 0 ? (
                  <div className="flex h-9 w-full items-center rounded-lg border border-input px-3 text-sm text-muted-foreground opacity-60">
                    {pagesLoading ? "Loading pages" : "No pages on this Meta user"}
                  </div>
                ) : (
                  <Select value={facebookPageId} onValueChange={(v) => v && setFacebookPageId(v)}>
                    <SelectTrigger id="launch-page">
                      <SelectValue placeholder="Choose a page" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {pages.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name ? `${p.name} (${shortId(p.id)})` : p.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <FieldRow>
                <Field label="Daily budget" htmlFor="launch-budget">
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
                      $
                    </span>
                    <Input
                      id="launch-budget"
                      type="number"
                      inputMode="decimal"
                      min={1}
                      step={1}
                      value={dailyBudget}
                      onChange={(e) => setDailyBudget(e.target.value)}
                      className="pl-7 tabular-nums"
                    />
                  </div>
                </Field>
                <Field label="Countries" htmlFor="launch-countries" help="Two-letter codes, comma separated">
                  <Input
                    id="launch-countries"
                    value={countries}
                    onChange={(e) => setCountries(e.target.value)}
                    placeholder="US, CA"
                    autoComplete="off"
                  />
                </Field>
              </FieldRow>
              <FieldRow>
                <Field
                  label="Objective"
                  htmlFor="launch-objective"
                  help={metaPixelId ? "Sales optimises for purchases through your pixel" : "Add a pixel in Meta settings to optimise for sales"}
                >
                  <Select value={objective} onValueChange={(v) => v && setObjective(v as "OUTCOME_TRAFFIC" | "OUTCOME_SALES")} disabled={!!target.campaignId}>
                    <SelectTrigger id="launch-objective">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="OUTCOME_TRAFFIC">Traffic (link clicks)</SelectItem>
                      <SelectItem value="OUTCOME_SALES" disabled={!metaPixelId}>
                        {metaPixelId ? "Sales (purchases)" : "Sales (purchases), needs a pixel"}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {target.campaignId && (
                    <p className="mt-1 text-xs text-muted-foreground">An existing campaign keeps its own objective.</p>
                  )}
                </Field>
                <Field label="Age range" htmlFor="launch-age-min" help="13 to 65">
                  <div className="flex items-center gap-2">
                    <Input id="launch-age-min" type="number" inputMode="numeric" min={13} max={65} value={ageMin} onChange={(e) => setAgeMin(e.target.value)} className="tabular-nums" aria-label="Minimum age" />
                    <span className="text-sm text-muted-foreground">to</span>
                    <Input id="launch-age-max" type="number" inputMode="numeric" min={13} max={65} value={ageMax} onChange={(e) => setAgeMax(e.target.value)} className="tabular-nums" aria-label="Maximum age" />
                  </div>
                </Field>
              </FieldRow>
              <MetaTargetPicker value={target} onChange={setTarget} idPrefix="launch" />
            </DashboardCard>
          </>
        }
        fillAside
        aside={
          <DashboardCard title="Launch" fill className="lg:flex-1" bodyClassName="flex flex-1 flex-col px-5 pb-5">
            <div className="flex gap-4">
              <span
                className={cn(
                  "flex h-24 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted text-muted-foreground",
                  ratioClass(selected ? getRatioLabel(selected.id) : "9:16")
                )}
              >
                {selected?.thumbnailUrl || (selected?.mediaType === "IMAGE" && selected.mediaUrl) ? (
                  <img
                    src={selected.thumbnailUrl || selected.mediaUrl || ""}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <IconPhoto size={18} stroke={1.5} />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-semibold text-foreground">
                  {selected ? selected.title?.trim() || "Untitled" : "Pick a piece of content"}
                </div>
                <div className="mt-0.5 truncate text-xs text-muted-foreground">
                  {selected?.destinationUrl?.replace(/^https?:\/\//, "") ?? "The ad links where the content points"}
                </div>
              </div>
            </div>
            <div className="mt-4">
              <AsideRow label="Program">{program ? program.title : "—"}</AsideRow>
              <AsideRow label="Commission">{program ? `${program.commissionRate}%` : "—"}</AsideRow>
              <AsideRow label="Page">{page ? page.name ?? shortId(page.id) : "—"}</AsideRow>
              <AsideRow label="Daily budget">
                {Number.isFinite(budget) && budget >= 1 ? formatAmount(budget) : "Meta default"}
              </AsideRow>
              <AsideRow label="Countries">{countryList.length ? countryList.join(", ") : "Meta default"}</AsideRow>
              <AsideRow label="Objective">{target.campaignId ? "From the campaign" : objective === "OUTCOME_SALES" ? "Sales" : "Traffic"}</AsideRow>
              <AsideRow label="Ages">{ageOk ? `${ageLo} to ${ageHi}` : "Fix the age range"}</AsideRow>
              <AsideRow label="Runs in">
                {target.adsetId ? target.adsetName ?? "Existing ad set" : target.campaignId ? `${target.campaignName ?? "Existing campaign"}, new ad set` : "New campaign"}
              </AsideRow>
            </div>
            <MetaLaunchPausedNotice adAccountId={defaultAdAccountId} className="mt-auto pt-4" />
            <Button type="button" className="mt-4 w-full" onClick={() => void handleLaunch()} disabled={!canLaunch}>
              {launching ? "Creating in Meta" : "Launch ad"}
            </Button>
          </DashboardCard>
        }
      />

      <DashboardCard
        title="Launched ads"
        bodyClassName="px-3 pb-3"
        action={
          programId ? (
            <button
              type="button"
              onClick={() => void loadAds()}
              disabled={adsLoading}
              className="rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              Refresh
            </button>
          ) : undefined
        }
      >
        <DataTable
          columns={launchedColumns}
          data={ads}
          isLoading={adsLoading}
          skeletonRows={4}
          getRowId={(row) => row.id}
          emptyText={programId ? "Nothing launched from this program yet" : "Choose a program to see its launched ads"}
          minTableWidth="min-w-0"
        />
      </DashboardCard>

      <ActionDialog
        open={newProgramOpen}
        onClose={() => setNewProgramOpen(false)}
        title="New Meta program"
        description="Affiliates whose content runs in these ads earn this commission on attributed sales."
        footer={
          <>
            <DialogTextButton onClick={() => setNewProgramOpen(false)} disabled={creatingProgram}>
              Cancel
            </DialogTextButton>
            <Button type="submit" form="new-meta-program" disabled={creatingProgram}>
              Create program
            </Button>
          </>
        }
      >
        <form id="new-meta-program" onSubmit={handleCreateProgram} className="space-y-4">
          <Field label="Name" htmlFor="new-program-title">
            <Input
              id="new-program-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Spring reels"
              autoComplete="off"
            />
          </Field>
          <Field label="Commission" htmlFor="new-program-rate">
            <div className="relative max-w-[10rem]">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
                %
              </span>
              <Input
                id="new-program-rate"
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step={0.5}
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                className="pl-7 tabular-nums"
              />
            </div>
          </Field>
        </form>
      </ActionDialog>
    </div>
  );
}
